/**
 * Reserved / GST book fields used by AccountForm. Extracted to a sibling file
 * to keep AccountForm.tsx under the max-lines threshold. Not part of the
 * public API.
 */

import { Input, Select, FormField, NumericInput } from '@elsapiens/ui'

import type { AccountFormData } from './chart-of-accounts-helpers'

const GST_COMPONENT_OPTIONS = [
  { value: '', label: '— Not a GST book —' },
  { value: 'cgst', label: 'CGST' },
  { value: 'sgst', label: 'SGST' },
  { value: 'igst', label: 'IGST' },
]

const GST_DIRECTION_OPTIONS = [
  { value: 'output', label: 'Output — collected on sales (liability)' },
  { value: 'input', label: 'Input / ITC — paid on purchases (asset)' },
]

interface ReservedBookFieldsProps {
  formData: AccountFormData
  setFormData: React.Dispatch<React.SetStateAction<AccountFormData>>
}

function ReservedTagField({ formData, setFormData }: ReservedBookFieldsProps): JSX.Element {
  return (
    <div className="grid grid-cols-1 el-gap-field">
      <FormField helperText="Advanced. Assign a built-in purpose (e.g. cgst_output, opening_balance). Must be unique." label="Reserved purpose tag">
        <Input
          hideMessage
          placeholder="e.g. cgst_output"
          value={formData.systemTag}
          onChange={e => setFormData(prev => ({ ...prev, systemTag: e.target.value.trim() }))}
        />
      </FormField>
    </div>
  )
}

function GstComponentField({ formData, setFormData }: ReservedBookFieldsProps): JSX.Element {
  return (
    <div className="grid grid-cols-1 el-gap-field">
      <FormField helperText="Mark this as a GST tax book so GST reports and set-off recognise it." label="GST component">
        <Select
          hideMessage
          options={GST_COMPONENT_OPTIONS}
          value={formData.gstComponent}
          onChange={value => setFormData(prev => ({
            ...prev,
            gstComponent: value,
            gstDirection: value ? (prev.gstDirection || 'output') : '',
            gstRate: value ? prev.gstRate : '',
          }))}
        />
      </FormField>
    </div>
  )
}

function GstDirectionAndRateFields({ formData, setFormData }: ReservedBookFieldsProps): JSX.Element {
  return (
    <div className="grid grid-cols-2 el-gap-field">
      <FormField label="Direction">
        <Select
          hideMessage
          options={GST_DIRECTION_OPTIONS}
          value={formData.gstDirection}
          onChange={value => setFormData(prev => ({ ...prev, gstDirection: value }))}
        />
      </FormField>
      <FormField helperText="Blank = all rates. Set e.g. 9 for a rate-wise book." label="GST rate %">
        <NumericInput
          hideMessage
          allowNegative={false}
          max={50}
          min={0}
          precision={2}
          suffix="%"
          value={formData.gstRate}
          onChange={v => setFormData(prev => ({ ...prev, gstRate: v !== null ? String(v) : '' }))}
        />
      </FormField>
    </div>
  )
}

export function ReservedBookSection({ formData, setFormData }: ReservedBookFieldsProps): JSX.Element {
  const isGST = formData.gstComponent !== ''
  return (
    <div className="-mx-[var(--el-container-px)] px-[var(--el-container-px)] border-t border-border pt-4 el-space-y-sm">
      <p className="text-xs font-semibold text-foreground">Reserved / GST book (optional)</p>
      <ReservedTagField formData={formData} setFormData={setFormData} />
      <GstComponentField formData={formData} setFormData={setFormData} />
      {isGST && <GstDirectionAndRateFields formData={formData} setFormData={setFormData} />}
    </div>
  )
}
